import React, { useCallback, useEffect, useState } from 'react';
import { Col, Form, Input, Modal, Row, Spin } from 'antd';
import PropTypes from 'prop-types';
import { useNotifications } from '../../../hooks/useNotifications';
import { CompaniesService } from '../../../services/companiesService';
import { FormWrapper } from '../../../components/formWrapper/form-wrapper';
import { Button } from '../../../components/buttons/buttons';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { noEmptyStringValidator } from '../../../utility/forms';
import { GooglePlacesAutocomplete } from '../../../components/geolocation/GooglePlacesAutocomplete';
import AlternativeEmails from '../../../components/alternativeEmails/AlternativeEmails';

export const ModalEditContact = ({ customerId, contactId, show, handleClose, onChangeContact }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorNotification, showSuccessNotification } = useNotifications();
  const { translate: t } = useDictionaryTranslation();

  const setFormData = useCallback(
    (contactData) => {
      form.setFieldsValue({
        name: contactData.name,
        reference: contactData.reference,
        phoneNumber: contactData.phone,
        email: contactData.email,
        identification: contactData.identification,
        address: { address: contactData.address, markerCoords: contactData.markerCoords },
        note: contactData.note,
        emailNotifications: contactData.emailNotifications,
      });
    },
    [form],
  );

  const getContactData = useCallback(async () => {
    setIsLoading(true);
    try {
      const contact = await CompaniesService.getContactById({ idContact: contactId, idCompany: customerId });
      setFormData(contact);
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorRetrievingRecord',
      });
    } finally {
      setIsLoading(false);
    }
  }, [setFormData, contactId, customerId, showErrorNotification]);

  const closeModal = ({ refreshData = false } = {}) => {
    form.resetFields();
    const modalParams = {
      refreshData,
      lastPage: !contactId,
    };
    handleClose(modalParams);
  };

  const getFormValues = () => {
    const values = form.getFieldValue();
    return {
      name: values.name,
      reference: values.reference,
      phone: values.phoneNumber,
      email: values.email,
      identification: values.identification,
      address: values.address?.address,
      markerCoords: values.address?.markerCoords,
      note: values.note,
      emailNotifications: values.emailNotifications,
    };
  };

  const updateContact = async () => {
    try {
      const contactData = getFormValues();
      const data = await CompaniesService.updateContact({
        idCompany: customerId,
        idContact: contactId,
        contactData,
      });
      onChangeContact?.(data);
      showSuccessNotification({
        title: 'success',
        description: 'successUpdatingRecord',
      });
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorUpdatingRecord',
      });
      throw error;
    }
  };

  const createContact = async () => {
    try {
      const contactData = getFormValues();
      const data = await CompaniesService.createContact({
        idCompany: customerId,
        contactData,
      });
      onChangeContact?.(data);

      showSuccessNotification({
        title: 'success',
        description: 'successCreatingRecord',
      });
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorCreatingRecord',
      });
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (contactId) await updateContact();
      else await createContact();
      closeModal({ refreshData: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (contactId) {
      getContactData();
    }
  }, [contactId]);

  return (
    <Modal
      maskClosable={false}
      title={t(contactId ? 'editContact' : 'createContact')}
      centered
      width="90%"
      height="70%"
      onCancel={closeModal}
      visible={show}
      footer={
        <div>
          <Button form="createContact" key="submit" htmlType="submit" type="primary" disabled={isLoading}>
            {t('save')}
          </Button>
        </div>
      }
    >
      <Spin spinning={isLoading}>
        <FormWrapper>
          <Form form={form} name="createContact" layout="vertical" onFinish={handleSubmit}>
            <Row>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  rules={[{ validator: noEmptyStringValidator, required: true, message: t('nameRequired') }]}
                  label={t('name')}
                >
                  <Input placeholder={t('namePlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="reference"
                  rules={[{ validator: noEmptyStringValidator, required: true, message: t('referenceRequired') }]}
                  label={t('reference')}
                >
                  <Input placeholder={t('referencePlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="identification" label={t('identification')}>
                  <Input placeholder={t('identificationPlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phoneNumber" label={t('phoneNumber')}>
                  <Input placeholder={t('phoneNumberPlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="address"
                  rules={[{ required: true, message: t('addressRequired') }]}
                  label={t('address')}
                >
                  <GooglePlacesAutocomplete />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label={t('mainEmail')}>
                  <Input placeholder={t('emailPlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item name="note" label={t('notes')}>
                  <Input.TextArea placeholder={t('enterNote')} />
                </Form.Item>
              </Col>

              <Col xs={12} md={12}>
                <Form.Item name="emailNotifications" label={t('alternativeEmail')} valuePropName="dataEmails">
                  <AlternativeEmails />
                </Form.Item>{' '}
              </Col>
            </Row>
          </Form>
        </FormWrapper>
      </Spin>
    </Modal>
  );
};

ModalEditContact.propTypes = {
  customerId: PropTypes.string.isRequired,
  contactId: PropTypes.string,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onChangeContact: PropTypes.func,
};
